import { PAGE_CACHE_HEADERS } from "@/lib/ssrCache";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import Article from "@/pages/Article";
import { articleQueryOptions, buildArticleHead, getArticleRedirectTarget } from "@/lib/articleQuery";
import { fetchRelatedArticleLinks } from "@/lib/listingLinks";
import { fetchRelatedArticles } from "@/components/RelatedArticles";

export const Route = createFileRoute("/articles/$slug")({
  loader: async ({ params, context }) => {
    // A transient database error used to render a body-less page with a 200.
    // Retry once, then fail loudly (5xx) so crawlers retry instead of indexing a shell.
    let article = null;
    try {
      article = await context.queryClient.ensureQueryData(articleQueryOptions(params.slug));
    } catch {
      await new Promise((r) => setTimeout(r, 300));
      article = await context.queryClient.fetchQuery(articleQueryOptions(params.slug));
    }
    if (!article) {
      const target = getArticleRedirectTarget(params.slug);
      if (target && target !== params.slug && /^[a-z0-9-]+$/i.test(target)) {
        const destination = await context.queryClient.ensureQueryData(articleQueryOptions(target));
        if (destination) throw redirect({ href: `/articles/${destination.slug}`, statusCode: 301 });
      }
      // Clean malformed crawler URLs: uppercase letters, appended
      // `"Read more about X"` text, or `:junk` suffixes from bad links.
      let raw = params.slug;
      try { raw = decodeURIComponent(raw); } catch { /* keep raw */ }
      const cleaned = raw.split(/["\u201C\u201D:\s]/)[0].replace(/-+$/, "").toLowerCase();
      if (cleaned && cleaned !== params.slug && /^[a-z0-9-]+$/.test(cleaned)) {
        const exists = await context.queryClient
          .ensureQueryData(articleQueryOptions(cleaned))
          .catch(() => null);
        if (exists) throw redirect({ href: `/articles/${cleaned}`, statusCode: 301 });
      }
      throw notFound();
    }
    const a = article as any;
    const [ssrLinks, relatedArticles] = await Promise.all([
      fetchRelatedArticleLinks(a?.category ?? null, params.slug, 12),
      a && (a.category || a.tags?.length)
        ? fetchRelatedArticles(a.id, a.category ?? null, a.tags ?? null).catch(() => undefined)
        : Promise.resolve(undefined),
    ]);
    return { article, ssrLinks, relatedArticles };
  },
  headers: () => PAGE_CACHE_HEADERS,
  head: ({ loaderData }) => buildArticleHead(loaderData?.article ?? null),
  component: ArticleRoute,
});

function ArticleRoute() {
  const { article, ssrLinks, relatedArticles } = Route.useLoaderData();
  return <Article initialArticle={article} ssrLinks={ssrLinks} relatedArticles={relatedArticles} />;
}
